import React, { useCallback, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { FiPrinter } from 'react-icons/fi';
import 'assets/css/print.css';
// eslint-disable-next-line no-restricted-imports
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PrintContent from 'app/containers/OrderDetails/HeaderOrderDetails/PrintOrder/PrintContent';
import { Workstation } from 'app/containers/OrderDetails/types';
import {
  createPartOrder,
  PartOrder,
  PartOrderJson,
} from 'app/models/PartOrder';
import { PARTORDER_URL } from 'app/containers/SiteWrapper/constants';
import { requestGet } from 'utils/requests';
import { refreshPartOrder } from 'app/containers/SiteWrapper/slice';
import useElectronEnvironment from 'hooks/useElectronEnvironment';
import ElectronPrintSelectionModal, {
  ElectronPrintSelection,
} from './ElectronPrintSelectionModal';
import { OrderPage } from '../../PackagesTab/LabelsToPrint/OrderPage';
import { selectAllCarriers } from 'store/slices/carriersSlice';
import { selectAllDestinations } from 'store/slices/destinationsSlice';
import { selectAllColors } from 'store/slices/colorSlice';
import electronPrintPipeline from 'services/printing/renderer/ElectronPrintPipeline';
import { ElectronPrintingApi } from 'services/printing/renderer/ElectronPrintingApi';
import {
  clearQueue,
  setError,
  setProcessing,
  setQueue,
  setWarnings,
} from 'services/printing/redux/printingSlice';
import { selectIsProcessing } from 'services/printing/redux/selectors';
import { useGetPickingOrderSettingsQuery } from 'services/kewloxApi';
import {
  DEFAULT_PICKING_ORDER_STRATEGY,
  PickingOrderStrategy,
} from 'shared/printing/pickingOrder';

class ComponentToPrint extends React.Component<{
  order: PartOrder;
  selection: ElectronPrintSelection;
  pickingOrderStrategy: PickingOrderStrategy;
}> {
  render() {
    const { order, selection, pickingOrderStrategy } = this.props;

    return (
      <div>
        {selection.labels && order.carrier?.name !== 'BPOST' && (
          <OrderPage partOrder={order} firstPage={true} />
        )}
        <PrintContent
          order={order}
          workstation={Workstation.Cabinet}
          showPicking={selection.picking}
          showLabels={selection.labels}
          pickingOrderStrategy={pickingOrderStrategy}
        />
        <PrintContent
          order={order}
          workstation={Workstation.Corner}
          showPicking={selection.picking}
          showLabels={selection.labels}
          pickingOrderStrategy={pickingOrderStrategy}
        />
        <PrintContent
          order={order}
          workstation={Workstation.Drawer}
          showPicking={selection.picking}
          showLabels={selection.labels}
          pickingOrderStrategy={pickingOrderStrategy}
        />
        <PrintContent
          order={order}
          workstation={Workstation.Accessory}
          showPicking={selection.picking}
          showLabels={selection.labels}
          pickingOrderStrategy={pickingOrderStrategy}
        />
      </div>
    );
  }
}

interface Props {
  order: PartOrder;
}

function PrintOrder({ order }: Props) {
  const dispatch = useDispatch();
  const componentRef = useRef<any>(null);
  const destinations = useSelector(selectAllDestinations);
  const carriers = useSelector(selectAllCarriers);
  const colors = useSelector(selectAllColors);
  const isQueueProcessing = useSelector(selectIsProcessing);
  const { isElectron } = useElectronEnvironment();
  // Refetched on mount so an admin switch applies without reloading the app.
  const { data: pickingOrderSettings } = useGetPickingOrderSettingsQuery(
    undefined,
    { refetchOnMountOrArgChange: true },
  );
  const pickingOrderStrategy =
    pickingOrderSettings?.strategy ?? DEFAULT_PICKING_ORDER_STRATEGY;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalProcessing, setIsModalProcessing] = useState(false);
  const [selection, setSelection] = useState<ElectronPrintSelection>({
    picking: true,
    labels: true,
    notices: true,
  });
  const modalProcessing = isModalProcessing || isQueueProcessing;

  const triggerLabel = isElectron
    ? 'Imprimer le picking, les étiquettes et les notices'
    : 'Imprimer le picking et les étiquettes';

  const handleAfterPrint = useCallback(async () => {
    const url = `${PARTORDER_URL}${order.id}/picking_list_printed/`;

    try {
      const partOrdersJson = (await requestGet(url)) as PartOrderJson;
      const newPartOrder = createPartOrder(
        partOrdersJson,
        destinations,
        carriers,
        colors,
      );
      dispatch(refreshPartOrder(newPartOrder));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh part order after print:', error);
    }
  }, [dispatch, order.id, destinations, carriers, colors]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    bodyClass: 'toprint',
    onAfterPrint: handleAfterPrint,
  });

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const noop = useCallback(() => undefined, []);

  const handleModalConfirm = useCallback(
    async (nextSelection: ElectronPrintSelection) => {
      setSelection(nextSelection);
      setIsModalProcessing(true);

      let queuePrepared = false;
      let combinedWarnings: string[] = [];

      try {
        const apiAvailable = await ElectronPrintingApi.isAvailable();
        if (!apiAvailable) {
          dispatch(setWarnings([]));
          dispatch(
            setError("Impression Ricoh indisponible. Vérifiez la configuration Electron."),
          );
          toast.error("Impression Ricoh indisponible. Réessayez après vérification de l'environnement.");
          return;
        }

        const preparation = await electronPrintPipeline.prepare(
          order,
          nextSelection,
          pickingOrderStrategy,
        );

        dispatch(setQueue(preparation.queue));
        dispatch(setProcessing(true));
        dispatch(setWarnings(preparation.warnings));
        setIsModalOpen(false);
        queuePrepared = true;

        const { result } = await electronPrintPipeline.print(preparation.jobs);

        combinedWarnings = Array.from(
          new Set([...(preparation.warnings ?? []), ...(result.warnings ?? [])]),
        );

        if (combinedWarnings.length > 0) {
          dispatch(setWarnings(combinedWarnings));
        } else {
          dispatch(setWarnings([]));
        }

        if (!result.success) {
          dispatch(
            setError("Échec de l'impression Ricoh. Consultez la file pour relancer."),
          );
          toast.error("Échec de l'impression Ricoh. Consultez la file pour relancer.");
          return;
        }

        if (combinedWarnings.length > 0) {
          combinedWarnings.forEach(message => toast.warn(message));
        }

        toast.success('Documents envoyés à l’imprimante Ricoh');
        await handleAfterPrint();
        dispatch(clearQueue());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Electron print pipeline failed:', error);
        if (queuePrepared) {
          dispatch(
            setError(
              "Erreur lors de l'impression Ricoh. Consultez la file pour relancer.",
            ),
          );
          if (combinedWarnings.length > 0) {
            dispatch(setWarnings(combinedWarnings));
          }
        } else {
          dispatch(setError("Erreur lors de la préparation de l'impression Ricoh."));
          dispatch(setWarnings([]));
        }
        toast.error("Erreur d'impression Ricoh. Consultez la file pour relancer.");
      } finally {
        setIsModalProcessing(false);
        setIsModalOpen(false);

        dispatch(setProcessing(false));
      }
    },
    [dispatch, handleAfterPrint, order, pickingOrderStrategy],
  );

  const handleWebPrintClick = useCallback(() => {
    handlePrint?.();
  }, [handlePrint]);

  return (
    <>
      <div className="mt-2 ml-2 flex items-center">
        {isElectron ? (
          <button
            type="button"
            className="mt-2 ml-2" 
            data-tip={triggerLabel}
            data-for="infos"
            aria-label={triggerLabel}
            onClick={openModal}
          >
            <FiPrinter className="h-6 w-6 ml-2" />
          </button>
        ) : (
          <button
            type="button"
            className="mt-2 ml-2"
            data-tip={triggerLabel}
            data-for="infos"
            aria-label={triggerLabel}
            onClick={handleWebPrintClick}
          >
            <FiPrinter className="h-6 w-6 ml-2" />
          </button>
        )}
      </div>

      {isElectron && (
        <ElectronPrintSelectionModal
          isOpen={isModalOpen}
          initialSelection={selection}
          onClose={modalProcessing ? noop : closeModal}
          onConfirm={handleModalConfirm}
          isProcessing={modalProcessing}
        />
      )}

      <div className="will-print" style={{ display: 'none' }}>
        <ComponentToPrint
          ref={componentRef}
          order={order}
          selection={selection}
          pickingOrderStrategy={pickingOrderStrategy}
        />
      </div>
    </>
  );
}

export default PrintOrder;
